import Element from "config/routes/Element";
import RedirectToRoute from "config/routes/RedirectToRoute";
import { lazy, Suspense } from "react";
import { Outlet, Route } from "react-router-dom";
import LoadingPage from "views/Base/LoadingPage";
import { COMPANY_ROUTES as ROUTES } from "./utils/companyRoutes";

const CompanyDetail = lazy(() => import("views/Company"));
const CompaniesAdd = lazy(() => import("views/Company/CompaniesAdd"));
const CompaniesList = lazy(() => import("views/Company/CompaniesList"));

const AccountingBalanceReview = lazy(() => import("views/Company/AccountingBalanceReview"));
const CompanySettings = lazy(() => import("views/Company/Settings"));
const CompanyUsers = lazy(() => import("views/Company/Users"));

const BankAccountForm = lazy(
  () => import("views/Company/General/Banks/components/BankAccountForm"),
);
const BankAccounts = lazy(() => import("views/Company/General/Banks/BankAccounts"));
const Banks = lazy(() => import("views/Company/General/Banks"));
const CompanyAddresses = lazy(() => import("views/Company/General/Addresses"));
const CompanyAssignments = lazy(() => import("views/Company/General/Assignments"));
const CompanyGeneralInfo = lazy(() => import("views/Company/General/Info"));

const CompanyDirectors = lazy(() => import("views/Company/Legal/Directors"));
const CompanyLegalInfo = lazy(() => import("views/Company/Legal/Info"));
const CompanyObligations = lazy(() => import("views/Company/Legal/Obligations/Obligations"));
const CompanyStakeholders = lazy(() => import("views/Company/Legal/Stakeholders"));
const CompanyTaxation = lazy(() => import("views/Company/Legal/Taxation"));
const CompanyVAT = lazy(() => import("views/Company/Legal/TVA"));

const CompanyBooks = lazy(() => import("views/Company/Accounting/Books"));
const CompanyCustomGraphics = lazy(() => import("views/Company/Accounting/CustomGraphics"));
const CompanyGeneralAccounts = lazy(() => import("views/Company/Accounting/General"));
const CompanyKeywords = lazy(() => import("views/Company/Accounting/Keywords"));
const CompanyListClients = lazy(() => import("views/Company/Accounting/Clients"));
const CompanyProviderList = lazy(() => import("views/Company/Accounting/Providers"));
const CompanySubsidiaries = lazy(() => import("views/Company/Accounting/Subsidiaries"));
const CompanyVatList = lazy(() => import("views/Company/Accounting/Vats"));
const Cycles = lazy(() => import("views/Company/Accounting/Cycles"));
const ExpensesCategories = lazy(() => import("views/Company/Accounting/ExpensesCategories"));
const ExportCompany = lazy(() => import("views/Company/Accounting/Export"));
const GeneralConstants = lazy(() => import("views/Company/Accounting/GeneralConstants"));
const ImportCompany = lazy(() => import("views/Company/Accounting/Import"));

const ProgressHistory = lazy(() => import("views/Company/FileProgress/ProgressHistory"));
const ProgressOCR = lazy(() => import("views/Company/FileProgress/ProgressOCR"));
const ProgressSSI = lazy(() => import("views/Company/FileProgress/ProgressSSI"));

export const companyRoutes = (
  <Route path={ROUTES.companies}>
    <Route
      index
      element={
        <Element requiredPermission="displayCompanies">
          <CompaniesList />
        </Element>
      }
    />

    <Route
      path={ROUTES.add}
      element={
        <Element requiredPermission="createCompanies">
          <CompaniesAdd />
        </Element>
      }
    />

    <Route
      path={ROUTES.companyDetail}
      element={
        <Suspense fallback={<LoadingPage />}>
          <CompanyDetail />
        </Suspense>
      }
    >
      <Route index element={<RedirectToRoute route={ROUTES.generalInfo} />} />

      {/* General — the section permission wraps every sub-page, as before. */}
      <Route
        path={ROUTES.general}
        element={
          <Element requiredPermission="canDisplayCompanyGeneral">
            <Outlet />
          </Element>
        }
      >
        <Route index element={<RedirectToRoute route={ROUTES.generalInfo} />} />
        <Route path={ROUTES.generalInfo} element={<CompanyGeneralInfo />} />
        <Route
          path={ROUTES.banks}
          element={
            <Element requiredPermission="canDisplayCompaniesBanks">
              <Banks />
            </Element>
          }
        />
        <Route
          path={ROUTES.bankAccounts}
          element={
            <Element requiredPermission="canDisplayCompaniesBanks">
              <BankAccounts />
            </Element>
          }
        />
        <Route
          path={ROUTES.bankAccountCreate}
          element={
            <Element requiredPermission="canDisplayCompaniesBanks">
              <BankAccountForm />
            </Element>
          }
        />
        <Route
          path={ROUTES.bankAccount}
          element={
            <Element requiredPermission="canDisplayCompaniesBanks">
              <BankAccountForm />
            </Element>
          }
        />
        <Route
          path={ROUTES.addresses}
          element={
            <Element requiredPermission="canDisplayCompanyAddress">
              <CompanyAddresses />
            </Element>
          }
        />
        <Route
          path={ROUTES.assignments}
          element={
            <Element requiredPermission="canDisplayAssignments">
              <CompanyAssignments />
            </Element>
          }
        />
      </Route>

      {/* Legal */}
      <Route path={ROUTES.legal}>
        <Route index element={<RedirectToRoute route={ROUTES.legalInfo} />} />
        <Route
          path={ROUTES.legalInfo}
          element={
            <Element requiredPermission="canDisplayCompanyLegal">
              <CompanyLegalInfo />
            </Element>
          }
        />
        <Route path={ROUTES.tax} element={<CompanyTaxation />} />
        <Route path={ROUTES.vat} element={<CompanyVAT />} />
        <Route
          path={ROUTES.legalDirectors}
          element={
            <Element requiredPermission="canDisplayCorporateDirectors">
              <CompanyDirectors />
            </Element>
          }
        />
        <Route
          path={ROUTES.legalStakeholders}
          element={
            <Element requiredPermission="canDisplayCorporateStakeholders">
              <CompanyStakeholders />
            </Element>
          }
        />
        <Route path={ROUTES.obligations} element={<CompanyObligations />} />
      </Route>

      <Route
        path={ROUTES.clients}
        element={
          <Element requiredPermission="displayUsers">
            <CompanyUsers />
          </Element>
        }
      />

      {/* Accounting */}
      <Route path={ROUTES.accounting}>
        <Route index element={<RedirectToRoute route={ROUTES.accountingGeneralCycles} />} />
        <Route
          path={ROUTES.accountingGeneralCycles}
          element={
            <Element requiredPermission="canDisplayCycles">
              <Cycles />
            </Element>
          }
        />
        <Route
          path={ROUTES.accountingBooks}
          element={
            <Element requiredPermission="canDisplayBooks">
              <CompanyBooks />
            </Element>
          }
        />
        <Route
          path={ROUTES.accountingVats}
          element={
            <Element requiredPermission="canDisplayVats">
              <CompanyVatList />
            </Element>
          }
        />
        <Route
          path={ROUTES.accountingClients}
          element={
            <Element requiredPermission="canDisplayClients">
              <CompanyListClients />
            </Element>
          }
        />
        <Route
          path={ROUTES.accountingProviders}
          element={
            <Element requiredPermission="canDisplayProviders">
              <CompanyProviderList />
            </Element>
          }
        />
        <Route
          path={ROUTES.accountingCounterparts}
          element={
            <Element requiredPermission="canDisplayCounterParts">
              <CompanyGeneralAccounts />
            </Element>
          }
        />
        <Route
          path={ROUTES.accountingSubsidiaries}
          element={
            <Element requiredPermission="canDisplaySubsidiaries">
              <CompanySubsidiaries />
            </Element>
          }
        />
        <Route path={ROUTES.accountingCustomGraphics} element={<CompanyCustomGraphics />} />
        <Route path={ROUTES.accountingKeywords} element={<CompanyKeywords />} />
        <Route path={ROUTES.accountingGeneralConstants} element={<GeneralConstants />} />
        <Route path={ROUTES.accountingExpensesCategories} element={<ExpensesCategories />} />
        <Route
          path={ROUTES.accountingImport}
          element={
            <Element requiredPermission="canImport">
              <ImportCompany />
            </Element>
          }
        />
        <Route
          path={ROUTES.accountingExport}
          element={
            <Element requiredPermission="canExport">
              <ExportCompany />
            </Element>
          }
        />
      </Route>

      <Route path={ROUTES.accountingBalanceReview} element={<AccountingBalanceReview />} />

      <Route
        path={ROUTES.settings}
        element={
          <Element requiredPermission="canDisplayCompanySettings">
            <CompanySettings />
          </Element>
        }
      />

      {/* File progress */}
      <Route path={ROUTES.fileProgress}>
        <Route index element={<RedirectToRoute route={ROUTES.fileProgressMl} />} />
        <Route path={ROUTES.fileProgressMl} element={<ProgressOCR />} />
        <Route path={ROUTES.fileProgressSsi} element={<ProgressSSI />} />
        <Route path={ROUTES.fileProgressHistory} element={<ProgressHistory />} />
      </Route>

      <Route path="*" element={<RedirectToRoute route={ROUTES.generalInfo} />} />
    </Route>
  </Route>
);
